import React, { useEffect, useState, useCallback } from 'react';
import confetti from 'canvas-confetti';
import { Trophy, X, Sparkle } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';

const CATEGORY_COLORS = {
  savings: '#2D6A4F',
  net_worth: '#C5A059',
  retirement: '#0A1128',
  wealth_health: '#C5A059',
  tax_savings: '#B7791F',
};

const LEVEL_COLORS = {
  Bronze: '#CD7F32',
  Silver: '#C0C0C0',
  Gold: '#C5A059',
  Platinum: '#E5E4E2',
  Legacy: '#0A1128',
};

const fireConfetti = (color = '#C5A059') => {
  // Professional, subtle two-burst pattern from bottom corners
  const navy = '#0A1128';
  const gold = '#C5A059';
  const accent = color;
  
  const palette = [gold, navy, accent, '#FFFFFF'];
  
  // Left burst
  confetti({
    particleCount: 40,
    angle: 60,
    spread: 55,
    origin: { x: 0.15, y: 0.85 },
    colors: palette,
    gravity: 0.9,
    scalar: 0.9,
    drift: 0.1,
  });
  
  // Right burst
  setTimeout(() => {
    confetti({
      particleCount: 40,
      angle: 120,
      spread: 55,
      origin: { x: 0.85, y: 0.85 },
      colors: palette,
      gravity: 0.9,
      scalar: 0.9,
      drift: -0.1,
    });
  }, 150);
};


const MilestoneCelebration = ({ celebrations, onClose, onAcknowledgeAll }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const current = celebrations[currentIndex];
  const isLast = currentIndex >= celebrations.length - 1;

  useEffect(() => {
    if (current) {
      const color = current.level
        ? LEVEL_COLORS[current.level] || '#C5A059'
        : CATEGORY_COLORS[current.category] || '#C5A059';
      fireConfetti(color);
    }
  }, [currentIndex, current]);

  if (!current) return null;

  const accentColor = current.level
    ? LEVEL_COLORS[current.level] || '#C5A059'
    : CATEGORY_COLORS[current.category] || '#C5A059';

  const handleNext = () => {
    if (isLast) {
      onAcknowledgeAll();
    } else {
      setCurrentIndex(currentIndex + 1);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 fade-in" data-testid="milestone-celebration-modal">
      <div className="bg-white rounded-md shadow-2xl max-w-md w-full overflow-hidden animate-slide-up">
        {/* Header */}
        <div style={{ background: `linear-gradient(135deg, #0A1128 0%, ${accentColor} 100%)` }} className="p-6 text-center relative">
          <button
            onClick={onClose}
            data-testid="celebration-close-button"
            className="absolute top-3 right-3 text-white/80 hover:text-white transition-colors"
          >
            <X size={20} />
          </button>
          <div className="inline-block p-3 bg-white/10 rounded-full mb-3 backdrop-blur-sm">
            <Trophy size={40} weight="duotone" className="text-[#C5A059]" />
          </div>
          <p className="text-xs uppercase tracking-[0.15em] text-white/70 mb-1">Milestone Achieved</p>
          <h2 className="cabinet-grotesk text-2xl font-medium text-white">
            {current.title}
          </h2>
          {current.level && (
            <div className="inline-block mt-3 px-3 py-1 bg-white/15 backdrop-blur-sm rounded text-xs font-semibold uppercase tracking-[0.1em]" style={{ color: LEVEL_COLORS[current.level] }}>
              {current.level} Level
            </div>
          )}
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          <p className="text-sm text-[#4A5568]" data-testid="celebration-description">{current.description}</p>

          {/* Motivational */}
          <div className="border-l-2 pl-4 py-1" style={{ borderColor: accentColor }}>
            <p className="text-[#1A202C] italic text-sm leading-relaxed">"{current.motivation}"</p>
          </div>

          {/* Educational */}
          <div className="bg-slate-50 rounded-md p-4">
            <div className="flex items-start gap-2">
              <Sparkle size={16} weight="duotone" className="text-[#C5A059] flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[#4A5568] mb-1">Educational Insight</p>
                <p className="text-xs text-[#1A202C] leading-relaxed">{current.education}</p>
              </div>
            </div>
          </div>

          {/* Disclaimer */}
          <p className="text-[10px] text-[#4A5568] leading-relaxed">
            Educational use only. Not personalized financial, tax, investment, or legal advice. Consult qualified professionals.
          </p>

          {/* Actions */}
          <div className="flex items-center justify-between gap-3 pt-2">
            {celebrations.length > 1 && (
              <span className="text-xs text-[#4A5568]">{currentIndex + 1} of {celebrations.length}</span>
            )}
            <Button
              onClick={handleNext}
              data-testid="celebration-next-button"
              className="flex-1 bg-[#0A1128] hover:bg-[#152145]"
            >
              {isLast ? 'Continue' : 'Next Milestone'}
            </Button>
          </div>
        </div>

        {/* Footer Branding */}
        <div className="bg-[#FDFCFB] border-t border-slate-200 px-6 py-3 text-center">
          <p className="text-[10px] text-[#4A5568]">
            <span className="text-[#0A1128] font-semibold">PLANIVANCE AI</span>
            <span className="text-[#A0AABF]"> · Outsource Solution Strategies, LLC</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default MilestoneCelebration;
